import { useRef, useState } from "react";
import {
  ALLOWED_TON_POOL_DENOMINATIONS,
  parseUnits,
  type CreatePoolController,
  type JettonMetadata,
} from "@zkresistor/shared";

export function useCreatePoolForm(controller: CreatePoolController) {
  const [kind, setKind] = useState<"ton" | "jetton">("ton");
  const [tonDenomination, setTonDenomination] = useState(ALLOWED_TON_POOL_DENOMINATIONS[0].toString());
  const [master, setMaster] = useState("");
  const [denomination, setDenomination] = useState("");
  const [resolved, setResolved] = useState<{ master: string; metadata: JettonMetadata } | null>(null);
  const [resolving, setResolving] = useState(false);
  const [resolutionError, setResolutionError] = useState<string | null>(null);
  const resolutionId = useRef(0);
  const validDenomination = (() => {
    if (resolved === null) return false;
    try {
      parseUnits(denomination, resolved.metadata.decimals);
      return true;
    } catch {
      return false;
    }
  })();

  const updateMaster = (value: string) => {
    resolutionId.current += 1;
    setMaster(value);
    setResolved(null);
    setResolving(false);
    setResolutionError(null);
  };

  const resolveMaster = async () => {
    const candidate = master.trim();
    if (candidate === "" || resolved !== null || resolving) return;
    const requestId = ++resolutionId.current;
    setResolving(true);
    setResolutionError(null);
    try {
      const result = await controller.resolveJetton(candidate);
      if (resolutionId.current === requestId) setResolved(result);
    } catch (error) {
      if (resolutionId.current === requestId) {
        setResolutionError(error instanceof Error ? error.message : String(error));
      }
    } finally {
      if (resolutionId.current === requestId) setResolving(false);
    }
  };

  const submit = () => {
    if (kind === "ton") {
      void controller.createTon(BigInt(tonDenomination));
      return;
    }
    void controller.createJetton(resolved?.master ?? "", denomination);
  };

  return {
    denomination,
    kind,
    master,
    resolved,
    resolutionError,
    resolving,
    setDenomination,
    setKind,
    setTonDenomination,
    submit,
    tonDenomination,
    updateMaster,
    validDenomination,
    resolveMaster,
  };
}
